import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import { useDateTimeFormat } from "./utils/formatters";
import StatusBadge from "./components/StatusBadge";
import {
  FaClipboardList, FaCheckCircle, FaTimesCircle, FaClock,
  FaUser, FaMapMarkerAlt, FaCreditCard, FaReceipt,
  FaSearch, FaEye, FaCheck, FaTimes, FaSave,
  FaExclamationCircle, FaPhoneAlt, FaKey,
  FaShieldAlt, FaLock, FaIdCard, FaTag,
  FaCalendarAlt, FaDollarSign,
} from "react-icons/fa";

const BASE   = API_BASE;
const ADMIN_FEE = 25.00;

const STATUS_META = {
  pending:  { label:"Pending Review", color:getBrandColors().orange, bg:"rgba(217,119,6,0.08)",  icon:<FaClock size={11}/> },
  active:   { label:"Active",         color:getBrandColors().green, bg:"rgba(5,150,105,0.08)",  icon:<FaCheckCircle size={11}/> },
  rejected: { label:"Rejected",       color:getBrandColors().red, bg:"rgba(220,38,38,0.08)",  icon:<FaTimesCircle size={11}/> },
};

const TABS = ["pending","active","rejected"];

const PURPOSE_META = {
  lane_membership:   { label:"Membership Plan",    color:"#6366f1", bg:"rgba(99,102,241,0.1)" },
  individual_booking:{ label:"Non-Member",         color:"#0ea5e9", bg:"rgba(14,165,233,0.1)" },
  team_membership:   { label:"Team Membership",    color:"#8b5cf6", bg:"rgba(139,92,246,0.1)" },
};

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const tone = status==="pending" ? "warning" : (status==="approved"||status==="active") ? "success" : "danger";
  return <StatusBadge label={m.label} tone={tone} icon={m.icon} style={{ padding: "3px 9px", fontSize: 10 }} />;
}

function PurposeChip({ purpose }) {
  const m = PURPOSE_META[purpose] || { label: purpose || "—", color:"#94a3b8", bg:"rgba(148,163,184,0.1)" };
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4,
                   padding:"3px 9px", borderRadius:20, fontSize:10, fontWeight:700,
                   background:m.bg, color:m.color, border:`1px solid ${m.color}44` }}>
      <FaTag size={9}/> {m.label}
    </span>
  );
}

/* ── Detail Panel ─────────────────────────────────────────────────────────── */
export function DetailPanel({ client, currentUser, membershipPlans, onClose, onRefresh }) {
  const { isDark } = useTheme();
  const { fmtDate } = useDateTimeFormat();

  // Once a customer has been approved (active) or rejected (inactive), the
  // record is final — no further field edits or re-approval/re-rejection.
  const isLocked = client.status !== "pending";

  const [form, setForm]               = useState({ ...client });
  const [saving, setSaving]           = useState(false);
  const [approving, setApproving]     = useState(false);
  const [showReject, setShowReject]   = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [msg, setMsg]                 = useState(null);

  // Billing overrides
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [adminFee,       setAdminFee]       = useState(ADMIN_FEE);
  const [extraFees,      setExtraFees]      = useState([]);
  const [specialDisc,    setSpecialDisc]    = useState({ amount:"", discType:"amt", label:"", recurring:false });
  const [newFeeLabel,    setNewFeeLabel]    = useState("");
  const [newFeeAmt,      setNewFeeAmt]      = useState("");
  const [newFeeRecur,    setNewFeeRecur]    = useState(false);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm({ ...client });
    setMsg(null);
    setShowReject(false);
    setRejectReason("");
    setSelectedPlanId(client.requested_plan_id ? String(client.requested_plan_id) : null);
    // Restore whatever was last saved via "Save Changes" — previously this
    // always reset to blank defaults, so even though nothing actually
    // persisted these fields, the fact that this ran again after every
    // save (Save Changes calls onRefresh(), which re-fetches `client` and
    // re-triggers this effect) meant they got wiped off the SCREEN too,
    // making "Save Changes" look like it silently discarded them.
    setAdminFee(client.admin_fee_draft != null ? client.admin_fee_draft : ADMIN_FEE);
    try { setExtraFees(client.extra_fees_json ? JSON.parse(client.extra_fees_json) : []); }
    catch { setExtraFees([]); }
    try {
      setSpecialDisc(client.special_discount_json
        ? JSON.parse(client.special_discount_json)
        : { amount:"", discType:"amt", label:"", recurring:false });
    } catch { setSpecialDisc({ amount:"", discType:"amt", label:"", recurring:false }); }
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
  }, [client]);

  const requestedPlan = membershipPlans.find(p => String(p.plan_id) === String(client.requested_plan_id)) || null;
  const activePlan = (selectedPlanId
    ? membershipPlans.find(p => String(p.plan_id) === String(selectedPlanId))
    : requestedPlan) || null;

  /* ── Billing calc (live with overrides) ── */
  const billing = (() => {
    const purpose = client.registration_purpose;
    const adminFeeAmt = parseFloat(adminFee) || 0;
    const extraOneTime  = extraFees.filter(f=>!f.recurring).reduce((s,f)=>s+parseFloat(f.amount||0),0);
    const extraRecurring= extraFees.filter(f=>f.recurring).reduce((s,f)=>s+parseFloat(f.amount||0),0);
    const rawDisc = parseFloat(specialDisc?.amount||0);
    const discIsRecur = specialDisc?.recurring||false;
    const discAmt = specialDisc?.discType==="pct"
      ? Math.round((discIsRecur ? (activePlan ? parseFloat(activePlan.price||0) : 0) : (activePlan ? parseFloat(activePlan.price||0) : 0)) * rawDisc / 100 * 100) / 100
      : rawDisc;
    const firstDisc = discIsRecur ? 0 : discAmt;
    const recurDisc = discIsRecur ? discAmt : 0;

    if (purpose === "lane_membership" && activePlan) {
      const price = parseFloat(activePlan.price || 0);
      const freq  = activePlan.billing_frequency || "monthly";
      const freqLabel = { once:"One-time", monthly:"Monthly", quarterly:"Quarterly", annually:"Annual" }[freq] || "Monthly";
      // The server (routers/lane_booking.py::approve_client) always prorates
      // the first charge to "today through end of the current calendar
      // month" and computes this itself, authoritatively, ignoring whatever
      // the client sends — so this MUST use the identical formula, or the
      // admin approves one number and the customer's card gets charged a
      // completely different one.
      const today = new Date();
      const daysInMonth = new Date(today.getFullYear(), today.getMonth()+1, 0).getDate();
      const daysLeft = daysInMonth - today.getDate() + 1;
      const proratedPrice = Math.round(price / daysInMonth * daysLeft * 100) / 100;
      const totalFirst = Math.max(0, proratedPrice + adminFeeAmt + extraOneTime - firstDisc);
      const recurring  = Math.max(0, price + extraRecurring - recurDisc);
      return { type:"membership", planName:activePlan.plan_name, price, proratedPrice, daysLeft, daysInMonth, freqLabel,
               adminFeeAmt, extraOneTime, extraRecurring, firstDisc, recurDisc,
               totalFirst, recurring };
    }
    if (purpose === "individual_booking") {
      const totalFirst = Math.max(0, adminFeeAmt + extraOneTime - firstDisc);
      return { type:"individual", adminFeeAmt, extraOneTime, firstDisc, totalFirst };
    }
    return null;
  })();

  const addFee = () => {
    if (!newFeeLabel.trim() || !newFeeAmt) return;
    setExtraFees(f=>[...f,{id:Date.now(),label:newFeeLabel.trim(),amount:parseFloat(newFeeAmt),recurring:newFeeRecur}]);
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
  };

  /* ── Styles ── */
  const inp = {
    width:"100%", boxSizing:"border-box", padding:"7px 10px",
    borderRadius:7, fontSize:13, outline:"none",
    border:`1.5px solid ${isDark?"rgba(255,255,255,0.15)":"#d1d5db"}`,
    background: isDark?"rgba(255,255,255,0.07)":"#fff",
    color: isDark?"#f1f5f9":"#1e293b", colorScheme:isDark?"dark":"light",
  };
  const card = (accent) => ({
    background: isDark?"rgba(255,255,255,0.04)":"#fff",
    border: `1px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
    borderLeft: `3px solid ${accent||getBrandColors().blue}`,
    borderRadius:10, padding:"12px 14px", marginBottom:10,
  });
  const secHead = (icon, title, sub2) => (
    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
      <div style={{ color:getBrandColors().blue, opacity:0.7 }}>{icon}</div>
      <div>
        <div style={{ fontSize:11, fontWeight:800, color:isDark?"#e2e8f0":getBrandColors().blue,
                      textTransform:"uppercase", letterSpacing:"0.06em" }}>{title}</div>
        {sub2&&<div style={{fontSize:10,color:"#94a3b8",marginTop:1}}>{sub2}</div>}
      </div>
    </div>
  );
  const lbl = (t) => (
    <div style={{ fontSize:10, fontWeight:700, color:isDark?"#94a3b8":"#64748b",
                  textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>{t}</div>
  );
  const BRow = ({ label, value, sub2, muted, bold, large, red }) => (
    <div style={{ display:"grid", gridTemplateColumns:"1fr auto", alignItems:"baseline",
                  padding:"4px 0", borderBottom:`1px dashed ${isDark?"rgba(255,255,255,0.07)":"#f1f5f9"}` }}>
      <div>
        <span style={{ fontSize:muted?11:12, color:muted?"#94a3b8":isDark?"#cbd5e1":"#475569", fontStyle:muted?"italic":"normal" }}>{label}</span>
        {sub2&&<div style={{fontSize:10,color:"#94a3b8",lineHeight:1.3}}>{sub2}</div>}
      </div>
      <span style={{ fontSize:large?17:bold?14:12, fontWeight:bold||large?800:500,
                     color:red?"#ef4444":large||bold?(isDark?getBrandColors().yellow:getBrandColors().blue):(isDark?"#e2e8f0":"#1e293b"),
                     textAlign:"right", whiteSpace:"nowrap" }}>{value}</span>
    </div>
  );

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    const res = await fetch(`${BASE}/lb/clients/${client.client_id}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ ...form, admin_fee_draft: adminFee, extra_fees: extraFees, special_discount: specialDisc }),
    });
    const data = await res.json();
    setSaving(false);
    setMsg({ ok:data.success, text:data.success?"Changes saved.":(data.message||"Save failed.") });
    if (data.success) onRefresh();
  };

  const handleApprove = async () => {
    setApproving(true); setMsg(null);
    // approve_client() re-reads the client's profile fields fresh from the
    // clients table — it never accepts them in this request body. Without
    // persisting `form` first, any correction typed on this screen that
    // wasn't separately saved via "Save Changes" is silently discarded.
    try {
      await fetch(`${BASE}/lb/clients/${client.client_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, admin_fee_draft: adminFee, extra_fees: extraFees, special_discount: specialDisc }),
      });
    } catch { /* non-fatal — approve still proceeds against last-saved data */ }
    try {
      const res = await fetch(`${BASE}/lb/clients/${client.client_id}/approve`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          action: "approve",
          reviewed_by: currentUser?.username || "admin",
          registration_notes: form.registration_notes || null,
          plan_id: selectedPlanId ? parseInt(selectedPlanId) : null,
          admin_fee: parseFloat(adminFee) || 0,
          extra_fees: extraFees,
          special_discount: specialDisc,
          total_first_payment: billing?.totalFirst ?? null,
          recurring_amount: billing?.recurring ?? null,
        }),
      });
      const data = await res.json();
      setApproving(false);
      if (data.success) {
        setMsg({ ok:true, text:"Customer activated successfully." });
        onRefresh("active");
        setTimeout(() => onClose(), 1500);
      } else {
        setMsg({ ok:false, text: data.detail || data.message || `Approval failed. (HTTP ${res.status})` });
      }
    } catch(err) {
      setApproving(false);
      setMsg({ ok:false, text:`Network error: ${err.message}` });
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { setMsg({ ok:false, text:"Enter a rejection reason." }); return; }
    try {
      const res = await fetch(`${BASE}/lb/clients/${client.client_id}/approve`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ action:"reject", reason:rejectReason, reviewed_by:currentUser?.username||"admin",
                               registration_notes: form.registration_notes || null }),
      });
      const data = await res.json();
      if (data.success) {
        setMsg({ ok:true, text:"Customer rejected." });
        setShowReject(false);
        onRefresh("inactive");
        setTimeout(() => onClose(), 1500);
      } else {
        setMsg({ ok:false, text: data.detail || data.message || `Reject failed. (HTTP ${res.status})` });
      }
    } catch(err) {
      setMsg({ ok:false, text:`Network error: ${err.message}` });
    }
  };

  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, width:540, zIndex:1000,
                  background:isDark?"#0f172a":"#f8fafc",
                  borderLeft:`2px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
                  overflowY:"auto", boxShadow:"-8px 0 32px rgba(0,0,0,0.18)",
                  display:"flex", flexDirection:"column",
                  fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

      {/* Header */}
      <div style={{ padding:"14px 16px", background:getBrandColors().blue, display:"flex",
                    alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:800, color:getBrandColors().yellow }}>{client.first_name} {client.last_name}</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", marginTop:1 }}>
            CLI-{String(client.client_id).padStart(5,"0")} · {fmtDate(client.created_at)}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <StatusChip status={client.status}/>
          <button onClick={onClose}
            style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:6,
                     color:"#fff", cursor:"pointer", padding:"5px 9px", fontSize:13 }}>
            <FaTimes/>
          </button>
        </div>
      </div>

      <div style={{ padding:"14px 16px", flex:1 }}>

        {isLocked && (
          <div style={{ padding:"9px 12px", borderRadius:8, marginBottom:10, fontSize:12,
                        display:"flex", alignItems:"center", gap:8,
                        background:isDark?"rgba(255,255,255,0.05)":"#f1f5f9",
                        color:isDark?"#94a3b8":"#64748b",
                        border:`1px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}` }}>
            <FaLock size={11}/> This customer is <strong>{client.status === "active" ? "approved" : client.status}</strong> — record is final and read-only.
          </div>
        )}

        <fieldset disabled={isLocked} style={{ border:"none", padding:0, margin:0 }}>

        {msg && (
          <div style={{ padding:"10px 12px", borderRadius:8, marginBottom:10, fontSize:12,
                        display:"flex", alignItems:"flex-start", gap:8,
                        background:msg.ok?"rgba(5,150,105,0.08)":"rgba(220,38,38,0.08)",
                        color:msg.ok?getBrandColors().green:getBrandColors().red,
                        border:`1px solid ${msg.ok?"#86efac":"#fca5a5"}` }}>
            {msg.ok ? <FaCheckCircle size={12} style={{flexShrink:0,marginTop:1}}/>
                    : <FaExclamationCircle size={12} style={{flexShrink:0,marginTop:1}}/>}
            <span>{msg.text}</span>
          </div>
        )}

        {/* ── 1. Registration Type ── */}
        <div style={card("#6366f1")}>
          {secHead(<FaTag size={12}/>, "Registration Type")}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: client.registration_purpose==="lane_membership"?10:0 }}>
            <PurposeChip purpose={client.registration_purpose}/>
            {requestedPlan && String(selectedPlanId)!==String(requestedPlan.plan_id) && (
              <span style={{ fontSize:10, color:"#94a3b8", fontStyle:"italic" }}>
                Requested: {requestedPlan.plan_name}
              </span>
            )}
          </div>
          {client.registration_purpose === "lane_membership" && (
            <div>
              {lbl("Membership Plan (admin can change)")}
              <select value={selectedPlanId||""} onChange={e=>setSelectedPlanId(e.target.value||null)}
                style={{ ...inp, background:isDark?"rgba(4,14,32,0.95)":"#fff" }}>
                <option value="">— No plan assigned —</option>
                {membershipPlans.filter(p=>p.active!==0).map(p=>(
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.plan_name} — ${parseFloat(p.price||0).toFixed(2)}/{p.billing_frequency||"mo"}
                    {p.max_weekly_hours>0 ? ` (${p.max_weekly_hours}h/wk)` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ── 2. Customer Details ── */}
        <div style={card(getBrandColors().yellow)}>
          {secHead(<FaUser size={12}/>, "Customer Details")}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px" }}>
            <div>{lbl("First Name")}<input value={form.first_name||""} onChange={e=>setF("first_name",e.target.value)} style={inp}/></div>
            <div>{lbl("Last Name")}<input value={form.last_name||""} onChange={e=>setF("last_name",e.target.value)} style={inp}/></div>
            <div>{lbl("Email")}<input value={form.email||""} onChange={e=>setF("email",e.target.value)} style={inp}/></div>
            <div>{lbl("Phone")}<input value={form.phone||""} onChange={e=>setF("phone",e.target.value)} style={inp}/></div>
            <div>{lbl("Date of Birth")}
              <input type="date" value={(form.date_of_birth||"").slice(0,10)}
                onChange={e=>setF("date_of_birth",e.target.value)} style={inp}/>
            </div>
            <div>{lbl("Gender")}
              <select value={form.gender||""} onChange={e=>setF("gender",e.target.value)}
                style={{ ...inp, background:isDark?"rgba(4,14,32,0.95)":"#fff" }}>
                <option value="">—</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${isDark?"rgba(255,255,255,0.08)":"#f1f5f9"}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
              <FaMapMarkerAlt size={10} color="#94a3b8"/>
              <span style={{ fontSize:10, fontWeight:800, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em" }}>Address</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px" }}>
              <div style={{gridColumn:"1/-1"}}>{lbl("Street Address")}<input value={form.street_address||""} onChange={e=>setF("street_address",e.target.value)} style={inp}/></div>
              <div>{lbl("City")}<input value={form.city||""} onChange={e=>setF("city",e.target.value)} style={inp}/></div>
              <div>{lbl("State")}<input value={form.state||""} onChange={e=>setF("state",e.target.value)} style={inp}/></div>
              <div>{lbl("Zip")}<input value={form.zip_code||""} onChange={e=>setF("zip_code",e.target.value)} style={inp}/></div>
            </div>
          </div>
        </div>

        {/* ── 3. Emergency Contact ── */}
        <div style={card("#a78bfa")}>
          {secHead(<FaPhoneAlt size={12}/>, "Emergency Contact")}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px" }}>
            <div style={{gridColumn:"1/-1"}}>{lbl("Full Name")}<input value={form.emergency_name||""} onChange={e=>setF("emergency_name",e.target.value)} style={inp}/></div>
            <div>{lbl("Phone")}<input value={form.emergency_phone||""} onChange={e=>setF("emergency_phone",e.target.value)} style={inp}/></div>
            <div>{lbl("Relationship")}<input value={form.emergency_relation||""} onChange={e=>setF("emergency_relation",e.target.value)} style={inp}/></div>
          </div>
        </div>

        {/* ── 4. Payment Card ── */}
        {(client.stripe_pm_id || client.card_last4) ? (
          <div style={card("#38bdf8")}>
            {secHead(<FaCreditCard size={12}/>, "Payment Card", "Card on file")}
            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 16px",
                          borderRadius:10, background:"rgba(56,189,248,0.07)",
                          border:"1px solid rgba(56,189,248,0.25)" }}>
              <div style={{ width:50, height:32, borderRadius:6, background:"#0c4a6e",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:10, fontWeight:800, color:"#fff", flexShrink:0 }}>
                {client.card_brand ? client.card_brand.toUpperCase().slice(0,4) : <FaCreditCard size={14}/>}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:isDark?"#e2e8f0":"#0c4a6e" }}>
                  •••• •••• •••• {client.card_last4||"????"}
                </div>
                {client.card_exp_month && client.card_exp_year && (
                  <div style={{ fontSize:12, color:isDark?"#94a3b8":"#64748b", marginTop:2 }}>
                    Expires {String(client.card_exp_month).padStart(2,"0")}/{String(client.card_exp_year).slice(-2)}
                  </div>
                )}
              </div>
              <span style={{ marginLeft:"auto", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700,
                             background:"rgba(5,150,105,0.12)", color:getBrandColors().green, border:"1px solid rgba(5,150,105,0.3)" }}>
                ✓ Verified
              </span>
            </div>
            <div style={{ fontSize:11, color:isDark?"#94a3b8":"#64748b", marginTop:8 }}>
              Card is saved with Stripe — never stored in our database.
            </div>
          </div>
        ) : (
          <div style={card("#38bdf8")}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10, gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <FaCreditCard size={12} color="#38bdf8"/>
                <span style={{ fontSize:12, fontWeight:700, color:isDark?"#e2e8f0":"#1e293b" }}>Payment Card</span>
                <span style={{ fontSize:11, color:isDark?"#64748b":"#94a3b8", marginLeft:4 }}>— No card captured</span>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                {[
                  { icon:<FaShieldAlt size={11} color="#22c55e"/>, label:"AES-256",      sub:"Encrypted"   },
                  { icon:<FaLock      size={11} color="#60a5fa"/>, label:"PCI DSS",      sub:"Certified"   },
                  { icon:<FaIdCard   size={11} color="#a78bfa"/>, label:"Zero Storage", sub:"Token only"  },
                ].map((b,i) => (
                  <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center",
                    padding:"5px 8px", borderRadius:8, minWidth:64, textAlign:"center",
                    background:isDark?"rgba(255,255,255,0.05)":"rgba(3,78,123,0.04)",
                    border:`1px solid ${isDark?"rgba(255,255,255,0.08)":"rgba(3,78,123,0.08)"}` }}>
                    {b.icon}
                    <div style={{ fontSize:9, fontWeight:800, color:isDark?"#94a3b8":"#64748b", lineHeight:1.3, marginTop:3 }}>{b.label}</div>
                    <div style={{ fontSize:9, color:isDark?"#64748b":"#94a3b8", lineHeight:1.2 }}>{b.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ fontSize:12, color:isDark?"#94a3b8":"#64748b" }}>
              No card was provided during registration. Card can be collected at first booking.
            </div>
          </div>
        )}

        {/* ── 5. Billing Overrides & Summary ── */}
        {billing && (
          <div style={card("#f59e0b")}>
            {secHead(<FaReceipt size={12}/>, "Billing Overrides & Summary", "Live — updates as you change values")}

            {/* Admin fee override */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"8px 12px", borderRadius:8, marginBottom:8,
                          background:isDark?"rgba(255,255,255,0.04)":"rgba(245,158,11,0.05)",
                          border:`1px solid ${isDark?"rgba(255,255,255,0.1)":"rgba(245,158,11,0.2)"}` }}>
              <span style={{ fontSize:12, color:isDark?"#e2e8f0":"#475569" }}>Administrative Fee</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13, color:"#94a3b8" }}>$</span>
                <input type="number" min={0} step="0.01"
                  value={adminFee} onChange={e=>setAdminFee(parseFloat(e.target.value)||0)}
                  style={{ ...inp, width:80, padding:"4px 8px", fontSize:13, textAlign:"right" }}/>
              </div>
            </div>

            {/* Extra fees */}
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:800, color:isDark?"#94a3b8":"#64748b",
                            textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>
                Additional Fees
              </div>
              {extraFees.map(f=>(
                <div key={f.id} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5,
                                         padding:"5px 9px", borderRadius:7,
                                         background:isDark?"rgba(255,255,255,0.05)":"rgba(245,158,11,0.04)",
                                         border:`1px solid ${isDark?"rgba(255,255,255,0.08)":"rgba(245,158,11,0.15)"}` }}>
                  <span style={{ flex:1, fontSize:12, color:isDark?"#e2e8f0":"#374151" }}>{f.label}</span>
                  <span style={{ fontSize:12, fontWeight:700, color:isDark?getBrandColors().yellow:getBrandColors().blue }}>${parseFloat(f.amount||0).toFixed(2)}</span>
                  <span style={{ fontSize:10, color:f.recurring?getBrandColors().green:"#94a3b8" }}>{f.recurring?"Recurring":"One-time"}</span>
                  <button onClick={()=>setExtraFees(fx=>fx.filter(x=>x.id!==f.id))}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", fontSize:12, padding:0 }}>
                    ×
                  </button>
                </div>
              ))}
              <div style={{ display:"flex", gap:6, alignItems:"flex-end", flexWrap:"wrap" }}>
                <input value={newFeeLabel} onChange={e=>setNewFeeLabel(e.target.value)}
                  placeholder="Fee label" onKeyDown={e=>e.key==="Enter"&&addFee()}
                  style={{ ...inp, flex:2, minWidth:100, padding:"5px 8px", fontSize:12 }}/>
                <input type="number" min={0} step="0.01" value={newFeeAmt}
                  onChange={e=>setNewFeeAmt(e.target.value)} placeholder="0.00"
                  onKeyDown={e=>e.key==="Enter"&&addFee()}
                  style={{ ...inp, width:80, padding:"5px 8px", fontSize:12 }}/>
                <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#94a3b8", whiteSpace:"nowrap" }}>
                  <input type="checkbox" checked={newFeeRecur} onChange={e=>setNewFeeRecur(e.target.checked)}/> Recurring
                </label>
                <button onClick={addFee}
                  style={{ padding:"5px 12px", borderRadius:7, border:"none", cursor:"pointer",
                           background:"rgba(245,158,11,0.2)", color:getBrandColors().orange, fontSize:12, fontWeight:700 }}>
                  + Add
                </button>
              </div>
            </div>

            {/* Special discount */}
            <div style={{ marginBottom:12, padding:"10px 12px", borderRadius:8,
                          background:isDark?"rgba(239,68,68,0.06)":"rgba(239,68,68,0.04)",
                          border:"1px solid rgba(239,68,68,0.15)" }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#ef4444",
                            textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>
                Special Discount
              </div>
              <div style={{ display:"flex", gap:4, marginBottom:8 }}>
                {[{v:"amt",lbl:"$ Fixed"},{v:"pct",lbl:"% Percent"}].map(opt=>(
                  <button key={opt.v} onClick={()=>setSpecialDisc(d=>({...d,discType:opt.v,amount:""}))}
                    style={{ flex:1, padding:"4px 0", borderRadius:6, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                             background:specialDisc.discType===opt.v?"#ef4444":"rgba(239,68,68,0.1)",
                             color:specialDisc.discType===opt.v?"#fff":"#ef4444" }}>
                    {opt.lbl}
                  </button>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 90px", gap:"6px 8px", alignItems:"end" }}>
                <div>{lbl("Label / Reason")}
                  <input value={specialDisc.label} onChange={e=>setSpecialDisc(d=>({...d,label:e.target.value}))}
                    placeholder="e.g. Loyalty, Family discount"
                    style={{ ...inp, padding:"5px 8px", fontSize:12 }}/>
                </div>
                <div>{lbl(specialDisc.discType==="pct"?"% Off":"Amount")}
                  <input type="number" min={0} step="0.01"
                    value={specialDisc.amount} onChange={e=>setSpecialDisc(d=>({...d,amount:e.target.value}))}
                    placeholder="0"
                    style={{ ...inp, padding:"5px 8px", fontSize:12 }}/>
                </div>
              </div>
              <label style={{ display:"flex", alignItems:"center", gap:6, marginTop:6, fontSize:11, color:"#94a3b8" }}>
                <input type="checkbox" checked={specialDisc.recurring}
                  onChange={e=>setSpecialDisc(d=>({...d,recurring:e.target.checked}))}/>
                Apply discount to recurring payments too
              </label>
            </div>

            {/* Summary */}
            <div style={{ padding:"12px 14px", borderRadius:9,
                          background:isDark?"rgba(245,158,11,0.07)":"rgba(245,158,11,0.06)",
                          border:"1px solid rgba(245,158,11,0.25)" }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#f59e0b", textTransform:"uppercase",
                             letterSpacing:"0.05em", marginBottom:10 }}>
                <FaCalendarAlt size={9} style={{marginRight:5}}/>First Payment (Due on Activation)
              </div>

              {billing.type === "membership" && (
                <BRow label={`${billing.planName} — Prorated`} sub2={`${billing.daysLeft}/${billing.daysInMonth} days @ $${billing.price.toFixed(2)}/mo`} value={`$${billing.proratedPrice.toFixed(2)}`}/>
              )}
              {billing.type === "individual" && (
                <BRow label="Individual Booking" sub2="Pay-per-use at time of booking" value="—" muted/>
              )}
              <BRow label="Administrative Fee" sub2="One-time" value={`$${billing.adminFeeAmt.toFixed(2)}`}/>
              {extraFees.filter(f=>!f.recurring).map(f=>(
                <BRow key={f.id} label={f.label} value={`$${parseFloat(f.amount||0).toFixed(2)}`}/>
              ))}
              {billing.firstDisc > 0 && (
                <BRow label={`Discount${specialDisc.label?` — ${specialDisc.label}`:""}`}
                      value={`-$${billing.firstDisc.toFixed(2)}`} red/>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", paddingTop:9, marginTop:4,
                             borderTop:`2px solid ${isDark?"rgba(255,255,255,0.12)":"#e2e8f0"}` }}>
                <span style={{ fontSize:14, fontWeight:800, color:isDark?"#f1f5f9":getBrandColors().blue }}>Total Charged Now</span>
                <span style={{ fontSize:20, fontWeight:900, color:isDark?getBrandColors().yellow:getBrandColors().blue }}>${billing.totalFirst.toFixed(2)}</span>
              </div>

              {billing.type === "membership" && (
                <div style={{ marginTop:12, paddingTop:10,
                               borderTop:`1px solid ${isDark?"rgba(255,255,255,0.07)":"#f1f5f9"}` }}>
                  <div style={{ fontSize:10, fontWeight:800, color:getBrandColors().green, textTransform:"uppercase",
                                 letterSpacing:"0.05em", marginBottom:6 }}>
                    <FaDollarSign size={9} style={{marginRight:4}}/>Recurring — {billing.freqLabel}
                  </div>
                  {extraFees.filter(f=>f.recurring).map(f=>(
                    <BRow key={f.id} label={f.label} value={`$${parseFloat(f.amount||0).toFixed(2)}`}/>
                  ))}
                  {billing.recurDisc > 0 && (
                    <BRow label={`Discount${specialDisc.label?` — ${specialDisc.label}`:""}`}
                          value={`-$${billing.recurDisc.toFixed(2)}`} red/>
                  )}
                  <div style={{ display:"flex", justifyContent:"space-between", paddingTop:4 }}>
                    <span style={{ fontSize:12, color:"#94a3b8" }}>Billed {billing.freqLabel}</span>
                    <span style={{ fontSize:15, fontWeight:800, color:getBrandColors().green }}>${billing.recurring.toFixed(2)}/mo</span>
                  </div>
                </div>
              )}
              {billing.type === "individual" && (
                <div style={{ marginTop:8, fontSize:11, color:isDark?"#94a3b8":"#64748b" }}>
                  Card on file will be charged at each individual lane booking.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 6. Waiver ── */}
        <div style={card(getBrandColors().red)}>
          {secHead(<FaKey size={12}/>, "Waiver")}
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {client.waiver_signature
              ? <><FaCheckCircle color={getBrandColors().green} size={14}/><span style={{fontSize:12,color:getBrandColors().green}}>Signed at registration</span></>
              : <><FaExclamationCircle color={getBrandColors().red} size={14}/><span style={{fontSize:12,color:getBrandColors().red}}>Not signed</span></>}
          </div>
        </div>

        {/* ── 7. Admin Notes ── */}
        <div style={card("#60a5fa")}>
          {secHead(<FaClipboardList size={12}/>, "Admin Notes", "Internal — not visible to customer")}
          <textarea value={form.registration_notes||""} onChange={e=>setF("registration_notes",e.target.value)}
            rows={3} placeholder="Add internal notes or instructions for the team…"
            style={{ ...inp, resize:"vertical", minHeight:70, fontFamily:"inherit" }}/>
        </div>

        {/* Rejection reason (read-only) */}
        {client.status === "rejected" && client.rejection_reason && (
          <div style={{ padding:"9px 12px", borderRadius:8, marginBottom:10,
                        background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.2)" }}>
            <div style={{ fontSize:10, fontWeight:700, color:getBrandColors().red, marginBottom:3 }}>REJECTION REASON</div>
            <div style={{ fontSize:12, color:"#991b1b" }}>{client.rejection_reason}</div>
          </div>
        )}

        {/* Active badge */}
        {client.status === "active" && (
          <div style={{ padding:"9px 12px", borderRadius:8, marginBottom:10,
                        background:"rgba(5,150,105,0.07)", border:"1px solid rgba(5,150,105,0.2)" }}>
            <div style={{ fontSize:10, fontWeight:700, color:getBrandColors().green, marginBottom:3 }}>ACTIVE CUSTOMER</div>
            <div style={{ fontSize:12, color:isDark?"#6ee7b7":"#065f46" }}>
              Approved by {client.reviewed_by||"admin"} on {fmtDate(client.reviewed_at)}
            </div>
          </div>
        )}

        {/* Reject input */}
        {showReject && (
          <div style={{ ...card(getBrandColors().red), background:"rgba(220,38,38,0.05)", marginBottom:10 }}>
            <div style={{ fontSize:10, fontWeight:700, color:getBrandColors().red, marginBottom:6 }}>REASON FOR REJECTION *</div>
            <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
              rows={2} placeholder="Enter reason for rejecting this registration…"
              style={{ ...inp, resize:"vertical", marginBottom:8 }}/>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleReject}
                style={{ flex:1, padding:"7px", borderRadius:7, border:"none", cursor:"pointer",
                         background:getBrandColors().red, color:"#fff", fontSize:12, fontWeight:700 }}>
                Confirm Reject
              </button>
              <button onClick={()=>setShowReject(false)}
                style={{ padding:"7px 14px", borderRadius:7, border:"none", cursor:"pointer",
                         background:isDark?"rgba(255,255,255,0.1)":"#e2e8f0",
                         color:isDark?"#e2e8f0":"#374151", fontSize:12 }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Post-rejection next-steps */}
        {client.status === "rejected" && (
          <div style={{ padding:"12px 14px", borderRadius:10, marginBottom:10,
                        background:"rgba(220,38,38,0.05)", border:"1px solid rgba(220,38,38,0.2)" }}>
            <div style={{ fontSize:11, fontWeight:800, color:getBrandColors().red, marginBottom:6, letterSpacing:1 }}>
              NEXT STEPS — REJECTED REGISTRATION
            </div>
            <div style={{ fontSize:12, color:isDark?"#fca5a5":"#991b1b", lineHeight:1.8 }}>
              <div>1. Customer has been notified via email (if configured).</div>
              <div>2. You may contact the customer to discuss the reason.</div>
              <div>3. Customer can re-submit a new registration after resolving the issue.</div>
              <div>4. This record is retained for audit purposes.</div>
            </div>
          </div>
        )}

        </fieldset>

        {/* Actions */}
        <div style={{ display:"flex", gap:8, marginTop:4 }}>
          {!isLocked && (
            <button onClick={handleSave} disabled={saving}
              style={{ flex:1, padding:"9px", borderRadius:8, border:"none", cursor:"pointer",
                       background:isDark?"rgba(255,255,255,0.1)":"#f1f5f9",
                       color:isDark?"#e2e8f0":"#374151", fontSize:12, fontWeight:700,
                       display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                       opacity:saving?0.6:1 }}>
              <FaSave size={11}/> {saving?"Saving…":"Save Changes"}
            </button>
          )}

          {client.status === "pending" && (
            <>
              <button onClick={handleApprove} disabled={approving}
                style={{ flex:2, padding:"9px", borderRadius:8, border:"none", cursor:"pointer",
                         background:`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`,
                         color:getBrandColors().blue, fontSize:13, fontWeight:900,
                         display:"flex", alignItems:"center", justifyContent:"center", gap:7,
                         opacity:approving?0.6:1 }}>
                <FaCheck size={12}/> {approving?"Activating…":"Approve & Activate"}
              </button>
              <button onClick={()=>{ setShowReject(v=>!v); setMsg(null); }}
                style={{ padding:"9px 13px", borderRadius:8, border:"none", cursor:"pointer",
                         background:"rgba(220,38,38,0.08)", color:getBrandColors().red, fontSize:12, fontWeight:700,
                         display:"flex", alignItems:"center", gap:6 }}>
                <FaTimes size={11}/> Reject
              </button>
            </>
          )}

          {client.status === "rejected" && (
            <div style={{ flex:1, padding:"9px 13px", borderRadius:8, fontSize:12, fontWeight:700,
                          background:"rgba(220,38,38,0.08)", color:getBrandColors().red,
                          display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                          border:"1px solid rgba(220,38,38,0.2)" }}>
              <FaTimesCircle size={13}/> Rejected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Approval Queue ──────────────────────────────────────────────────── */
export default function LaneCustomerApproval({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();

  const [activeTab,       setActiveTab]       = useState("pending");
  const [clients,         setClients]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [selected,        setSelected]        = useState(null);
  const [counts,          setCounts]          = useState({ pending:0, active:0, rejected:0 });
  const [membershipPlans, setMembershipPlans] = useState([]);

  useEffect(() => {
    fetch(`${BASE}/lb/membership-plans`)
      .then(r=>r.json())
      .then(d=>setMembershipPlans(d.plans||[]))
      .catch(()=>{});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/lb/clients?status=${activeTab}&is_student=0`);
      const data = await res.json();
      setClients(Array.isArray(data) ? data : (data.clients||[]));
    } catch { setClients([]); }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { load(); setSelected(null); }, [load]);

  const loadCounts = useCallback(() => {
    Promise.all(
      TABS.map(s =>
        fetch(`${BASE}/lb/clients?status=${s}&is_student=0`)
          .then(r=>r.json())
          .then(d => ({ status:s, count:(Array.isArray(d)?d:(d.clients||[])).length }))
          .catch(() => ({ status:s, count:0 }))
      )
    ).then(results => {
      const c = {};
      results.forEach(r => { c[r.status] = r.count; });
      setCounts(c);
    });
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts, clients]);

  const filtered = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.first_name||"").toLowerCase().includes(q) ||
      (c.last_name||"").toLowerCase().includes(q) ||
      (c.email||"").toLowerCase().includes(q)
    );
  });

  const cardBg = isDark?"rgba(255,255,255,0.04)":"#fff";
  const border  = isDark?"rgba(255,255,255,0.1)":"#e2e8f0";

  return (
    <div style={{ padding:"20px 24px" }}>

      {/* Page header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20,
                    padding:"14px 18px", borderRadius:12,
                    background:"rgba(255,255,255,0.15)", backdropFilter:"blur(14px)", WebkitBackdropFilter: "blur(14px)",
                    border:"1px solid rgba(255,255,255,0.25)", width:"fit-content" }}>
        <FaClipboardList size={22} color={getBrandColors().blue}/>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:isDark?"#f1f5f9":getBrandColors().blue }}>Lane Customer Approval Queue</div>
          <div style={{ fontSize:12, color:T.textMuted||"#94a3b8" }}>Review and activate lane booking customers</div>
        </div>
      </div>

      {/* KPI chips */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16, maxWidth:480 }}>
        {TABS.map(t => {
          const m = STATUS_META[t];
          return (
            <div key={t} onClick={()=>setActiveTab(t)}
              style={{ background:activeTab===t?m.bg:cardBg,
                       border:`1.5px solid ${activeTab===t?m.color+"60":border}`,
                       borderRadius:10, padding:"10px 14px", cursor:"pointer", transition:"all 0.15s" }}>
              <div style={{ fontSize:11, color:m.color, fontWeight:700, marginBottom:3 }}>{m.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:isDark?"#f1f5f9":"#1e293b" }}>{counts[t]||0}</div>
            </div>
          );
        })}
      </div>

      {/* Tab bar + search */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    marginBottom:12, gap:12, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:6 }}>
          {TABS.map(t => {
            const m = STATUS_META[t];
            const active = activeTab === t;
            return (
              <button key={t} onClick={()=>setActiveTab(t)}
                style={{ padding:"6px 14px", borderRadius:20, border:"none", cursor:"pointer",
                         fontSize:12, fontWeight:700,
                         background:active?`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`:(isDark?"rgba(255,255,255,0.08)":"#f1f5f9"),
                         color:active?getBrandColors().blue:(isDark?"#94a3b8":"#64748b") }}>
                {m.label}
                {counts[t]>0 && (
                  <span style={{ marginLeft:4, padding:"1px 6px", borderRadius:10,
                    background:active?getBrandColors().blue+"30":(isDark?"rgba(255,255,255,0.1)":"#e2e8f0"),
                    color:active?getBrandColors().blue:"#94a3b8", fontSize:10 }}>{counts[t]}</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={()=>{ load(); loadCounts(); }}
            style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${border}`, cursor:"pointer",
                     background:cardBg, color:isDark?"#94a3b8":"#64748b",
                     fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:6 }}>
            ↻ Refresh
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:8, background:cardBg,
                        border:`1px solid ${border}`, borderRadius:8, padding:"6px 12px" }}>
            <FaSearch size={11} color="#94a3b8"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{ border:"none", outline:"none", background:"transparent",
                       fontSize:13, color:T.textPrimary||"#1e293b", width:200 }}/>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"200px 1fr 160px 120px 90px",
                      padding:"8px 16px", borderBottom:`1px solid ${border}`,
                      background:isDark?"rgba(255,255,255,0.03)":"#f8fafc" }}>
          {["Customer","Email","Registration Type","Submitted",""].map(h => (
            <div key={h} style={{ fontSize:10, fontWeight:800, color:"#94a3b8",
                                   textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding:"32px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:"32px", textAlign:"center" }}>
            <FaClipboardList size={28} color="#94a3b8" style={{marginBottom:8,opacity:0.4}}/>
            <div style={{ color:"#94a3b8", fontSize:13 }}>
              {search ? "No matches found." : `No ${activeTab} customers.`}
            </div>
          </div>
        ) : filtered.map((c, i) => (
          <div key={c.client_id} onClick={() => setSelected(c)}
            style={{ display:"grid", gridTemplateColumns:"200px 1fr 160px 120px 90px",
                     padding:"10px 16px", cursor:"pointer",
                     borderBottom:i<filtered.length-1?`1px solid ${border}`:"none",
                     background:selected?.client_id===c.client_id
                       ?(isDark?"rgba(3,78,123,0.15)":"#eff6ff")
                       :(i%2===0?"transparent":(isDark?"rgba(255,255,255,0.015)":"#fafafa")),
                     transition:"background 0.12s" }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:isDark?"#f1f5f9":"#1e293b" }}>
                {c.first_name} {c.last_name}
              </div>
              <div style={{ fontSize:10, color:"#94a3b8" }}>CLI-{String(c.client_id).padStart(5,"0")}</div>
            </div>
            <div style={{ fontSize:12, color:isDark?"#e2e8f0":"#374151", display:"flex", alignItems:"center" }}>
              {c.email}
            </div>
            <div style={{ display:"flex", alignItems:"center" }}>
              <PurposeChip purpose={c.registration_purpose}/>
            </div>
            <div style={{ fontSize:11, color:"#94a3b8", display:"flex", alignItems:"center" }}>
              {fmtDate(c.created_at)}
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:6 }}>
              <StatusChip status={c.status}/>
              <FaEye size={12} color="#94a3b8"/>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <>
          <div onClick={()=>setSelected(null)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:999 }}/>
          <DetailPanel
            client={selected}
            currentUser={currentUser}
            membershipPlans={membershipPlans}
            onClose={()=>setSelected(null)}
            onRefresh={(newStatus) => {
              // If the client was approved or rejected, switch to the new tab
              if (newStatus === "active")   { setActiveTab("active");   }
              if (newStatus === "inactive") { setActiveTab("rejected"); }
              load();
              loadCounts();
              // Re-fetch the client so the panel reflects the updated status
              fetch(`${BASE}/lb/clients/${selected.client_id}`)
                .then(r=>r.json())
                .then(d=>{ if(d && d.client_id) setSelected(d); })
                .catch(()=>{});
            }}
          />
        </>
      )}
    </div>
  );
}
